/**
 * Auth Storage Service
 *
 * Centralized service for managing authentication data in localStorage.
 * This eliminates scattered localStorage calls and provides type-safe access to auth data.
 *
 * @module authStorage
 */

import {
  MODULE_KEY_MAPPING,
  PERMISSION_TO_MODULE,
} from '../constants/moduleMapping.constants';

// ─────────────────────────────────────────────────────────────────────────────
// TYPE DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────

export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber: string;
  role: string;
  roles: string[];
}

export interface TenantData {
  id: number;
  code: string;
  name: string;
  role: string;
  applicationId: number;
  applicationCode: string;
  applicationName: string;
}

export interface RestaurantData {
  id: number;
  name: string;
  locationId: number;
  locationName: string;
  isActive: boolean;
}

export interface SubscriptionData {
  id: number;
  planName: string;
  expiresAt: string;
  daysRemaining: number;
  isActive: boolean;
  status: string;
}

export interface AccessData {
  modules?: string[] | any[]; // Plan modules from subscription (can be string[] or ModuleDetail[])
  permissions: string[];
  isOwner: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  hasAllLocations: boolean;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token: string;
  user: UserData;
  tenant: TenantData;
  restaurant: RestaurantData;
  subscription: SubscriptionData;
  access: AccessData;
}

// ─────────────────────────────────────────────────────────────────────────────
// STORAGE KEYS - Centralized key management
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEYS = {
  // Auth tokens
  TOKEN: "token",
  AUTH_TOKEN: "authToken",

  // User data
  USER_ID: "userId",
  USER_EMAIL: "userEmail",
  USER_PHONE: "userPhone",
  USER_FIRST_NAME: "userFirstName",
  USER_LAST_NAME: "userLastName",
  USER_FULL_NAME: "userFullName",
  USER_TYPE: "userType",
  ROLE: "role",
  USER_ROLES: "userRoles",

  // Tenant data
  TENANT_ID: "tenantId",
  TENANT_CODE: "tenantCode",
  TENANT_NAME: "tenantName",

  // Restaurant data
  RESTAURANT_ID: "restaurantId",
  BUSINESS_ID: "businessId",
  LOYALTY_RESTAURANT_ID: "loyaltyRestaurantId",
  RESTAURANT_NAME: "restaurant_name",
  BUSINESS_NAME: "business_name",

  // Subscription data
  PLAN: "plan",
  PLAN_STATUS: "plan_status",
  HAS_ACTIVE_SUBSCRIPTION: "hasActiveSubscription",
  DAYS_REMAINING: "daysRemaining",
  SUBSCRIPTION_STATUS: "subscriptionStatus",

  // Access control
  GRANTED_PERMISSIONS: "grantedPermissions",
  INCLUDED_MODULES: "includedModules",
  IS_OWNER: "isOwner",
  IS_ADMIN: "isAdmin",
  IS_SUPER_ADMIN: "isSuperAdmin",
  HAS_ALL_LOCATIONS: "hasAllLocations",

  // Legacy/other
  FORCE_LOGIN_PAGE: "forceLoginPage",
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// AUTH STORAGE SERVICE
// ─────────────────────────────────────────────────────────────────────────────

class AuthStorageService {
  /**
   * Store complete login response data
   */
  storeLoginData(loginResponse: LoginResponse): void {
    const { token, user, tenant, restaurant, subscription, access } =
      loginResponse;

    // Store tokens
    this.setToken(token);

    // Record when this login data was written. SessionManager uses this to skip
    // an immediately-redundant background refresh right after a fresh login.
    localStorage.setItem("sessionTimestamp", Date.now().toString());

    // Store user data
    if (user) {
      this.setUserData(user);
    }

    // Store tenant data
    if (tenant) {
      this.setTenantData(tenant);
    }

    // Store restaurant data
    if (restaurant) {
      this.setRestaurantData(restaurant);
    }

    // Store subscription data
    if (subscription) {
      this.setSubscriptionData(subscription);
    }

    // Store access control data
    if (access) {
      this.setAccessData(access);
    }
  }

  // ─── Token Management ────────────────────────────────────────────────────

  setToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  }

  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  // ─── User Data Management ────────────────────────────────────────────────

  setUserData(user: UserData): void {
    localStorage.setItem(STORAGE_KEYS.USER_ID, user.id || "");
    localStorage.setItem(STORAGE_KEYS.USER_EMAIL, user.email || "");
    localStorage.setItem(STORAGE_KEYS.USER_PHONE, user.phoneNumber || "");
    localStorage.setItem(STORAGE_KEYS.USER_FIRST_NAME, user.firstName || "");
    localStorage.setItem(STORAGE_KEYS.USER_LAST_NAME, user.lastName || "");
    localStorage.setItem(
      STORAGE_KEYS.USER_FULL_NAME,
      `${user.firstName || ""} ${user.lastName || ""}`.trim(),
    );
    localStorage.setItem(STORAGE_KEYS.USER_TYPE, "admin");

    // Store roles
    const primaryRole = user.role || user.roles?.[0] || "user";
    const userRoles = user.roles || [];

    localStorage.setItem(STORAGE_KEYS.ROLE, primaryRole.toLowerCase());
    localStorage.setItem(STORAGE_KEYS.USER_ROLES, JSON.stringify(userRoles));

    // Remove force login flag
    localStorage.removeItem(STORAGE_KEYS.FORCE_LOGIN_PAGE);
  }

  getUserData(): Partial<UserData> | null {
    const id = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (!id) return null;

    return {
      id,
      email: localStorage.getItem(STORAGE_KEYS.USER_EMAIL) || "",
      firstName: localStorage.getItem(STORAGE_KEYS.USER_FIRST_NAME) || "",
      lastName: localStorage.getItem(STORAGE_KEYS.USER_LAST_NAME) || "",
      fullName: localStorage.getItem(STORAGE_KEYS.USER_FULL_NAME) || "",
      phoneNumber: localStorage.getItem(STORAGE_KEYS.USER_PHONE) || "",
      role: localStorage.getItem(STORAGE_KEYS.ROLE) || "",
      roles: this.getUserRoles(),
    };
  }

  getUserId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USER_ID);
  }

  getUserEmail(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  }

  getUserFullName(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USER_FULL_NAME);
  }

  getUserPhone(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USER_PHONE);
  }

  getPrimaryRole(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ROLE);
  }

  getUserRoles(): string[] {
    try {
      const rolesStr = localStorage.getItem(STORAGE_KEYS.USER_ROLES);
      return rolesStr ? JSON.parse(rolesStr) : [];
    } catch {
      return [];
    }
  }

  // ─── Tenant Data Management ──────────────────────────────────────────────

  setTenantData(tenant: TenantData): void {
    localStorage.setItem(STORAGE_KEYS.TENANT_ID, tenant.id.toString());
    localStorage.setItem(STORAGE_KEYS.TENANT_CODE, tenant.code);
    localStorage.setItem(STORAGE_KEYS.TENANT_NAME, tenant.name);
  }

  getTenantData(): Partial<TenantData> | null {
    const id = localStorage.getItem(STORAGE_KEYS.TENANT_ID);
    if (!id) return null;

    return {
      id: parseInt(id),
      code: localStorage.getItem(STORAGE_KEYS.TENANT_CODE) || "",
      name: localStorage.getItem(STORAGE_KEYS.TENANT_NAME) || "",
    };
  }

  getTenantId(): number | null {
    const id = localStorage.getItem(STORAGE_KEYS.TENANT_ID);
    return id ? parseInt(id) : null;
  }

  // ─── Restaurant Data Management ──────────────────────────────────────────

  setRestaurantData(restaurant: RestaurantData): void {
    if (!restaurant?.id || restaurant.id <= 0) {
      localStorage.removeItem(STORAGE_KEYS.RESTAURANT_ID);
      localStorage.removeItem(STORAGE_KEYS.BUSINESS_ID);
      localStorage.removeItem(STORAGE_KEYS.LOYALTY_RESTAURANT_ID);
      localStorage.removeItem(STORAGE_KEYS.RESTAURANT_NAME);
      localStorage.removeItem(STORAGE_KEYS.BUSINESS_NAME);
      return;
    }
    const restaurantId = restaurant.id.toString();
    localStorage.setItem(STORAGE_KEYS.RESTAURANT_ID, restaurantId);
    localStorage.setItem(STORAGE_KEYS.BUSINESS_ID, restaurantId);
    localStorage.setItem(STORAGE_KEYS.LOYALTY_RESTAURANT_ID, restaurantId);
    localStorage.setItem(STORAGE_KEYS.RESTAURANT_NAME, restaurant.name);
    localStorage.setItem(STORAGE_KEYS.BUSINESS_NAME, restaurant.name);
  }

  getRestaurantData(): Partial<RestaurantData> | null {
    const id = localStorage.getItem(STORAGE_KEYS.RESTAURANT_ID);
    if (!id) return null;

    return {
      id: parseInt(id),
      name: localStorage.getItem(STORAGE_KEYS.RESTAURANT_NAME) || "",
    };
  }

  getRestaurantId(): number | null {
    const id = localStorage.getItem(STORAGE_KEYS.RESTAURANT_ID);
    return id ? parseInt(id) : null;
  }

  getBusinessId(): number | null {
    const id = localStorage.getItem(STORAGE_KEYS.BUSINESS_ID);
    return id ? parseInt(id) : null;
  }

  getRestaurantName(): string | null {
    return (
      localStorage.getItem(STORAGE_KEYS.RESTAURANT_NAME) ||
      localStorage.getItem(STORAGE_KEYS.BUSINESS_NAME)
    );
  }

  // ─── Subscription Data Management ────────────────────────────────────────

  setSubscriptionData(subscription: SubscriptionData): void {
    const planName = subscription.planName?.toLowerCase() || "free trial";
    const isActive = subscription.isActive;

    localStorage.setItem(STORAGE_KEYS.PLAN, planName);
    localStorage.setItem(
      STORAGE_KEYS.PLAN_STATUS,
      isActive ? "active" : "inactive",
    );
    localStorage.setItem(
      STORAGE_KEYS.HAS_ACTIVE_SUBSCRIPTION,
      isActive.toString(),
    );
    localStorage.setItem(
      STORAGE_KEYS.DAYS_REMAINING,
      subscription.daysRemaining?.toString() || "0",
    );
    localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION_STATUS, subscription.status);
  }

  getSubscriptionData(): Partial<SubscriptionData> | null {
    const plan = localStorage.getItem(STORAGE_KEYS.PLAN);
    if (!plan) return null;

    return {
      planName: plan,
      isActive:
        localStorage.getItem(STORAGE_KEYS.HAS_ACTIVE_SUBSCRIPTION) === "true",
      daysRemaining: parseInt(
        localStorage.getItem(STORAGE_KEYS.DAYS_REMAINING) || "0",
      ),
      status: localStorage.getItem(STORAGE_KEYS.SUBSCRIPTION_STATUS) || "",
    };
  }

  getPlanName(): string | null {
    return localStorage.getItem(STORAGE_KEYS.PLAN);
  }

  hasActiveSubscription(): boolean {
    return (
      localStorage.getItem(STORAGE_KEYS.HAS_ACTIVE_SUBSCRIPTION) === "true"
    );
  }

  getDaysRemaining(): number {
    return parseInt(localStorage.getItem(STORAGE_KEYS.DAYS_REMAINING) || "0");
  }

  // ─── Access Control Management ───────────────────────────────────────────

  setAccessData(access: AccessData): void {
    // Store permissions first (needed for module derivation fallback)
    const permissions = access.permissions || [];
    localStorage.setItem(
      STORAGE_KEYS.GRANTED_PERMISSIONS,
      JSON.stringify(permissions),
    );

    // Store modules — backend sends an array of ModuleDetail objects or strings.
    // Free Trial plans return modules: [] (empty), so fall back to deriving
    // modules from the permissions list so route guards still work.
    if (access.modules && access.modules.length > 0) {
      const moduleKeys = this.extractModuleKeys(access.modules);
      localStorage.setItem(
        STORAGE_KEYS.INCLUDED_MODULES,
        JSON.stringify(moduleKeys),
      );

      // Also store full module details if available (for UI display)
      if (typeof access.modules[0] === "object") {
        localStorage.setItem("moduleDetails", JSON.stringify(access.modules));
      }
    } else if (permissions.length > 0) {
      // Backend returned empty modules (e.g. Free Trial) — derive from permissions
      const derivedModules = this.deriveModulesFromPermissions(permissions);
      localStorage.setItem(
        STORAGE_KEYS.INCLUDED_MODULES,
        JSON.stringify(derivedModules),
      );
    }

    // Store permission flags
    localStorage.setItem(
      STORAGE_KEYS.IS_OWNER,
      access.isOwner?.toString() || "false",
    );
    localStorage.setItem(
      STORAGE_KEYS.IS_ADMIN,
      access.isAdmin?.toString() || "false",
    );
    localStorage.setItem(
      STORAGE_KEYS.IS_SUPER_ADMIN,
      access.isSuperAdmin?.toString() || "false",
    );
    localStorage.setItem(
      STORAGE_KEYS.HAS_ALL_LOCATIONS,
      access.hasAllLocations?.toString() || "false",
    );
  }

  /**
   * Extract module keys from modules array (handles both ModuleDetail[] and string[])
   */
  private extractModuleKeys(modules: any[]): string[] {
    if (!modules || modules.length === 0) return [];

    const rawKeys: string[] =
      typeof modules[0] === "object" && modules[0].moduleKey
        ? modules.map((m: any) => m.moduleKey)
        : (modules as string[]);

    const normalized = new Set<string>();
    for (const key of rawKeys) {
      // Use the shared MODULE_KEY_MAPPING from moduleMapping.constants.ts
      normalized.add(MODULE_KEY_MAPPING[key] ?? key);
    }

    return Array.from(normalized);
  }

  /**
   * Derive frontend module keys from permission strings.
   * Used when backend returns empty modules array (e.g. Free Trial plans).
   * Maps permission prefixes to the same frontend module keys used by route guards.
   */
  private deriveModulesFromPermissions(permissions: string[]): string[] {
    const modules = new Set<string>();
    for (const permission of permissions) {
      // Exact match in PERMISSION_TO_MODULE (e.g. "STAFF_VIEW" → "USERS")
      if (PERMISSION_TO_MODULE[permission]) {
        modules.add(PERMISSION_TO_MODULE[permission]);
        continue;
      }
      // Fallback: try the first segment as a module key (e.g. "STAFF_VIEW" → "STAFF" → "USERS")
      const prefix = permission.split("_")[0];
      const prefixMapped = PERMISSION_TO_MODULE[prefix] ?? MODULE_KEY_MAPPING[prefix];
      if (prefixMapped) {
        modules.add(prefixMapped);
      }
    }
    return Array.from(modules);
  }

  setModules(modules: string[]): void {
    localStorage.setItem(
      STORAGE_KEYS.INCLUDED_MODULES,
      JSON.stringify(modules),
    );
  }

  getPermissions(): string[] {
    try {
      const permissionsStr = localStorage.getItem(
        STORAGE_KEYS.GRANTED_PERMISSIONS,
      );
      return permissionsStr ? JSON.parse(permissionsStr) : [];
    } catch {
      return [];
    }
  }

  getModules(): string[] {
    try {
      const modulesStr = localStorage.getItem(STORAGE_KEYS.INCLUDED_MODULES);
      return modulesStr ? JSON.parse(modulesStr) : [];
    } catch {
      return [];
    }
  }

  isOwner(): boolean {
    return localStorage.getItem(STORAGE_KEYS.IS_OWNER) === "true";
  }

  isAdmin(): boolean {
    return localStorage.getItem(STORAGE_KEYS.IS_ADMIN) === "true";
  }

  isSuperAdmin(): boolean {
    return localStorage.getItem(STORAGE_KEYS.IS_SUPER_ADMIN) === "true";
  }

  hasAllLocations(): boolean {
    return localStorage.getItem(STORAGE_KEYS.HAS_ALL_LOCATIONS) === "true";
  }

  /**
   * Get full module details (with permissions for each module)
   */
  getModuleDetails(): any[] {
    try {
      const detailsStr = localStorage.getItem("moduleDetails");
      return detailsStr ? JSON.parse(detailsStr) : [];
    } catch {
      return [];
    }
  }

  // ─── Utilities ───────────────────────────────────────────────────────────

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Clear all authentication data from localStorage
   */
  clearAll(): void {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    // Also clear auxiliary keys that live outside STORAGE_KEYS
    localStorage.removeItem("sessionTimestamp");
    localStorage.removeItem("moduleDetails");
  }

  /**
   * Get all stored auth data (for debugging)
   */
  getAllStoredData(): Record<string, any> {
    const data: Record<string, any> = {};
    Object.entries(STORAGE_KEYS).forEach(([key, storageKey]) => {
      const value = localStorage.getItem(storageKey);
      if (value) {
        try {
          data[key] = JSON.parse(value);
        } catch {
          data[key] = value;
        }
      }
    });
    return data;
  }

  /**
   * Log current auth state (development only)
   */
  debugAuthState(): void {
    if (process.env.NODE_ENV === "development") {
      console.group("🔐 Auth Storage State");
      console.groupEnd();
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT SINGLETON INSTANCE
// ─────────────────────────────────────────────────────────────────────────────

export const authStorage = new AuthStorageService();

// Export storage keys for reference
export { STORAGE_KEYS };
